import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { journalAPI } from "../../services/api";
import AccountsPage from "./accounts/AccountsPage";
import JournalPage from "./journal/JournalPage";
import {
  LayoutDashboard, Wallet, BookOpen,
  TrendingUp, TrendingDown, ShoppingCart,
  DollarSign, ArrowLeftRight, RefreshCw,
} from "lucide-react";

type Tab = "dashboard" | "accounts" | "journal";
type Period = "3" | "6" | "12";

// ماه‌های شمسی افغانی
const JALALI_MONTHS = ["حمل","ثور","جوزا","سرطان","اسد","سنبله","میزان","عقرب","قوس","جدی","دلو","حوت"];

interface Stats {
  total_purchases: number;
  total_sales: number;
  total_expenses: number;
  total_cogs: number;
  total_receipts: number;
  total_payments: number;
  total_receivables: number;
  total_payables: number;
  sales_by_currency: Record<string, number>;
  purchases_by_currency: Record<string, number>;
  receipts_by_currency: Record<string, number>;
  payments_by_currency: Record<string, number>;
  expenses_by_currency: Record<string, number>;
  cogs_by_currency: Record<string, number>;
  receivables_by_currency: Record<string, number>;
  payables_by_currency: Record<string, number>;
  chart_data: { month: string; jalali_month: string; sales: number; purchases: number; expenses: number; cogs: number }[];
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// نمایش مبالغ به تفکیک ارز
function CurrencyBreakdown({ byCurrency, color }: { byCurrency: Record<string, number>; color: string }) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) return <div style={{ fontSize: 20, fontWeight: 900, color }}>—</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {entries.map(([cur, amt]) => (
        <div key={cur} style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color }}>{fmt(amt)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color, opacity: 0.7 }}>{cur}</span>
        </div>
      ))}
    </div>
  );
}

// تبدیل ماه میلادی به شمسی
function getJalaliMonthLabel(gregorianMonth: string): string {
  try {
    const [year, month] = gregorianMonth.split("-").map(Number);
    // تبدیل تقریبی ماه میلادی به شمسی (ماه 1 میلادی ≈ ماه 10 شمسی)
    const jalaliMonthIndex = ((month + 8) % 12);
    return JALALI_MONTHS[jalaliMonthIndex];
  } catch {
    return gregorianMonth;
  }
}

export default function Finance() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab]       = useState<Tab>("dashboard");
  const [stats, setStats]   = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>("12");

  useEffect(() => {
    if (tab === "dashboard") {
      loadStats();
      // auto-refresh هر ۱۰ دقیقه
      const interval = setInterval(() => { loadStats(); }, 10 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [tab, period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await journalAPI.dashboardStats({ period: parseInt(period) });
      if (res.success) setStats(res.stats);
    } catch {}
    setLoading(false);
  };

  const TABS = [
    { id: "dashboard" as Tab, label: "داشبورد مالی",   icon: LayoutDashboard, color: "#3b82f6" },
    { id: "accounts"  as Tab, label: "مدیریت حساب‌ها", icon: Wallet,          color: "#10b981" },
    { id: "journal"   as Tab, label: "جورنال",          icon: BookOpen,        color: "#8b5cf6" },
  ];

  const CARDS = stats ? [
    { label: "خریدهای این ماه",     byCurrency: stats.purchases_by_currency,  color: "#f59e0b", icon: ShoppingCart   },
    { label: "فروشات این ماه",      byCurrency: stats.sales_by_currency,       color: "#10b981", icon: TrendingUp     },
    { label: "مصارف این ماه",       byCurrency: stats.expenses_by_currency,    color: "#ef4444", icon: TrendingDown   },
    { label: "COGS این ماه",        byCurrency: stats.cogs_by_currency,        color: "#8b5cf6", icon: DollarSign     },
    { label: "دریافتی‌های این ماه", byCurrency: stats.receipts_by_currency,    color: "#3b82f6", icon: TrendingUp     },
    { label: "پرداختی‌های این ماه", byCurrency: stats.payments_by_currency,    color: "#f97316", icon: TrendingDown   },
    { label: "طلب‌های شرکت",        byCurrency: stats.receivables_by_currency, color: "#06b6d4", icon: DollarSign     },
    { label: "قرضداری‌های شرکت",    byCurrency: stats.payables_by_currency,    color: "#dc2626", icon: ArrowLeftRight },
  ] : [];

  const chartData = stats?.chart_data || [];
  const maxVal = chartData.length > 0
    ? Math.max(...chartData.flatMap(d => [d.sales, d.purchases, d.expenses, d.cogs]), 1)
    : 1;

  const chartBars = [
    { key: "sales",     label: "فروش",   color: "#10b981" },
    { key: "purchases", label: "خرید",   color: "#f59e0b" },
    { key: "expenses",  label: "مصارف",  color: "#ef4444" },
    { key: "cogs",      label: "COGS",   color: "#8b5cf6" },
  ];

  return (
    <div style={{ padding: "28px 28px 60px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>
          💰 سیستم مالی
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>مدیریت مالی</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>مدیریت حساب‌ها، جورنال مالی و گزارشات</p>
      </div>

      {/* Tabs */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 28, padding: 6,
        background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
        border: `1px solid ${T.cardBorder}`, borderRadius: 14,
      }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, padding: "11px 16px", borderRadius: 10, border: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 700,
            fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
            background: tab === t.id ? `linear-gradient(135deg,${t.color},${t.color}bb)` : "transparent",
            color: tab === t.id ? "#fff" : T.subtext,
            transition: "all 0.2s",
            boxShadow: tab === t.id ? `0 4px 14px ${t.color}40` : "none",
          }}>
            <t.icon size={16} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === "dashboard" && (
        <>
          {loading ? (
            <div style={{ textAlign: "center", padding: 80, color: T.subtext }}>
              <div style={{
                width: 44, height: 44, margin: "0 auto 16px",
                border: "3px solid rgba(59,130,246,0.2)",
                borderTop: "3px solid #3b82f6",
                borderRadius: "50%", animation: "spin 0.8s linear infinite",
              }} />
              در حال بارگذاری...
            </div>
          ) : (
            <>
              {/* Stat Cards */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 14, marginBottom: 28,
              }}>
                {CARDS.map((card, i) => (
                  <div key={i} style={{
                    borderRadius: 16, padding: "20px",
                    background: dark
                      ? "linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))"
                      : "#fff",
                    border: `1px solid ${T.cardBorder}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "default",
                    position: "relative", overflow: "hidden",
                  }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.transform = "translateY(-3px)";
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 32px ${card.color}25`;
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.transform = "none";
                      (e.currentTarget as HTMLElement).style.boxShadow = "none";
                    }}
                  >
                    {/* رنگ پس‌زمینه دایره‌ای */}
                    <div style={{
                      position: "absolute", top: -20, left: -20,
                      width: 80, height: 80, borderRadius: "50%",
                      background: `${card.color}08`,
                    }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, position: "relative" }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        background: `linear-gradient(135deg,${card.color}22,${card.color}10)`,
                        border: `1px solid ${card.color}30`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: `0 4px 12px ${card.color}20`,
                      }}>
                        <card.icon size={20} color={card.color} />
                      </div>
                      <div style={{ fontSize: 12, color: T.subtext, fontWeight: 600, lineHeight: 1.4 }}>
                        {card.label}
                      </div>
                    </div>
                    <div style={{ position: "relative" }}>
                      <CurrencyBreakdown byCurrency={card.byCurrency} color={card.color} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div style={{
                borderRadius: 20, padding: "28px",
                background: dark
                  ? "linear-gradient(135deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))"
                  : "linear-gradient(135deg,#ffffff,#f8faff)",
                border: `1px solid ${T.cardBorder}`,
                boxShadow: dark ? "none" : "0 8px 32px rgba(0,0,0,0.06)",
              }}>
                {/* Chart Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>📊 نمودار مقایسه‌ای معاملات</div>
                    <div style={{ fontSize: 12, color: T.subtext, marginTop: 4 }}>خرید، فروش، مصارف و COGS</div>
                  </div>

                  {/* انتخاب دوره */}
                  <div style={{ display: "flex", gap: 6, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: 4 }}>
                    {([["3","3 ماهه"],["6","6 ماهه"],["12","12 ماهه"]] as [Period,string][]).map(([val, lbl]) => (
                      <button key={val} onClick={() => setPeriod(val)} style={{
                        padding: "7px 14px", borderRadius: 8, border: "none",
                        cursor: "pointer", fontSize: 12, fontWeight: 700,
                        fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                        background: period === val ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : "transparent",
                        color: period === val ? "#fff" : T.subtext,
                        transition: "all 0.2s",
                        boxShadow: period === val ? "0 3px 10px rgba(59,130,246,0.3)" : "none",
                      }}>{lbl}</button>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div style={{ display: "flex", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
                  {chartBars.map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: 4,
                        background: `linear-gradient(135deg,${b.color},${b.color}aa)`,
                        boxShadow: `0 2px 6px ${b.color}40`,
                      }} />
                      <span style={{ fontSize: 12, color: T.subtext, fontWeight: 600 }}>{b.label}</span>
                    </div>
                  ))}
                </div>

                {/* Bars */}
                {chartData.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: T.subtext }}>داده‌ای موجود نیست</div>
                ) : (
                  <div style={{ position: "relative" }}>
                    {/* خطوط راهنما افقی */}
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 200, pointerEvents: "none" }}>
                      {[0,25,50,75,100].map(p => (
                        <div key={p} style={{ position: "absolute", bottom: `${p}%`, left: 0, right: 0, borderTop: `1px dashed ${dark?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.06)"}` }} />
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 220, overflowX: "auto", paddingBottom: 4 }}>
                    {chartData.map((d: any, i: number) => (
                      <div key={i} style={{ flex: 1, minWidth: 52, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 185, width: "100%" }}>
                          {chartBars.map((bar, j) => {
                            const val = (d as any)[bar.key] || 0;
                            const h = Math.max((val / maxVal) * 185, val > 0 ? 4 : 0);
                            return (
                              <div key={j} title={`${bar.label}: $${fmt(val)}`} style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", justifyContent: "flex-end", cursor: "pointer" }}
                                onMouseEnter={e => { const el = e.currentTarget.firstChild as HTMLElement; if(el){el.style.filter="brightness(1.3)";el.style.transform="scaleY(1.03) scaleX(1.05)";} }}
                                onMouseLeave={e => { const el = e.currentTarget.firstChild as HTMLElement; if(el){el.style.filter="none";el.style.transform="none";} }}
                              >
                                <div style={{
                                  height: `${h}px`,
                                  background: `linear-gradient(180deg,${bar.color}ff,${bar.color}55)`,
                                  borderRadius: "6px 6px 0 0",
                                  transition: "height 0.7s cubic-bezier(0.34,1.56,0.64,1), filter 0.2s, transform 0.2s",
                                  boxShadow: h > 10 ? `0 -4px 12px ${bar.color}50` : "none",
                                  minHeight: val > 0 ? 4 : 0,
                                  transformOrigin: "bottom",
                                }} />
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 10, color: T.subtext, textAlign: "center", fontWeight: 600, whiteSpace: "nowrap" as const }}>{d.jalali_month || getJalaliMonthLabel(d.month)}</div>
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {tab === "accounts" && <AccountsPage />}
      {tab === "journal"  && <JournalPage />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}